import type {
  ChamberMemberProInvoiceTaxCategorySettingsReturn,
  ChamberMemberProProratedSignupPriceReturn,
} from "../../../graphql/types";
import {
  ChamberMemberProMembershipPricingModelEnum,
  ChamberMemberProPaymentScheduleEnum,
  MonthEnum,
} from "../../../graphql/types";

type MembershipPriceRange = {
  id?: string;
  code?: string;
  from: number;
  to?: number | null;
  annualPrice?: number;
  monthlyPrice?: number;
  annualPriceInDollars?: number;
  monthlyPriceInDollars?: number;
};

export type MembershipPricingModelForCalculation = {
  id: string;
  code?: string;
  pricingModel: ChamberMemberProMembershipPricingModelEnum | "employeeRange" | "fixed";
  annualPrice?: number;
  monthlyPrice?: number;
  annualPriceInDollars?: number;
  monthlyPriceInDollars?: number;
  ranges?: MembershipPriceRange[];
};

type MembershipBasePriceInput = {
  fullTimeEmployees: string;
  membershipModel?: MembershipPricingModelForCalculation;
  partTimeEmployees: string;
  partTimeEmployeesPerFullTime?: number;
  paymentSchedule: ChamberMemberProPaymentScheduleEnum | "";
};

export type MembershipBasePriceResult = {
  amountInCents: number;
  amountInDollars: number;
  code: string;
  pricingKey: string;
  weightedEmployees: number;
};

type MembershipEffectivePriceInput = {
  basePrice: MembershipBasePriceResult;
  paymentSchedule: ChamberMemberProPaymentScheduleEnum | "";
  proratedSignupPricingEnabled?: boolean;
  proratedSignupPrices?: Pick<
    ChamberMemberProProratedSignupPriceReturn,
    "month" | "priceInDollars" | "pricingKey"
  >[];
};

type MembershipInvoiceTaxSettingsForCalculation = Pick<
  ChamberMemberProInvoiceTaxCategorySettingsReturn,
  | "enabled"
  | "firstTaxRatePercentage"
  | "secondTaxRatePercentage"
>;

export type MembershipEffectivePriceResult = MembershipBasePriceResult & {
  baseAmountInCents: number;
  baseAmountInDollars: number;
  proratedDiscountInCents: number;
  proratedDiscountInDollars: number;
};

const monthValues = [
  MonthEnum.January,
  MonthEnum.February,
  MonthEnum.March,
  MonthEnum.April,
  MonthEnum.May,
  MonthEnum.June,
  MonthEnum.July,
  MonthEnum.August,
  MonthEnum.September,
  MonthEnum.October,
  MonthEnum.November,
  MonthEnum.December,
];

const getCurrentMonth = () => monthValues[new Date().getUTCMonth()];

const getPositiveNumber = (value: string | number | null | undefined) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
};

export const getMembershipInvoiceTaxRatePercentage = ({
  invoiceTaxSettings,
}: {
  invoiceTaxSettings?: MembershipInvoiceTaxSettingsForCalculation | null;
}) => {
  if (!invoiceTaxSettings?.enabled) {
    return 0;
  }

  return (
    getPositiveNumber(invoiceTaxSettings.firstTaxRatePercentage) +
    getPositiveNumber(invoiceTaxSettings.secondTaxRatePercentage)
  );
};

const getPriceInDollars = (
  source: {
    annualPrice?: number;
    monthlyPrice?: number;
    annualPriceInDollars?: number;
    monthlyPriceInDollars?: number;
  },
  paymentSchedule: ChamberMemberProPaymentScheduleEnum | ""
) => {
  const annualPrice = source.annualPriceInDollars ?? source.annualPrice ?? 0;
  const monthlyPrice = source.monthlyPriceInDollars ?? source.monthlyPrice;

  return paymentSchedule === ChamberMemberProPaymentScheduleEnum.Monthly
    ? monthlyPrice ?? annualPrice
    : annualPrice;
};

export const getWeightedEmployeeCount = ({
  fullTimeEmployees,
  partTimeEmployees,
  partTimeEmployeesPerFullTime,
}: {
  fullTimeEmployees: string;
  partTimeEmployees: string;
  partTimeEmployeesPerFullTime?: number;
}) => {
  const partTimeConversionRate =
    partTimeEmployeesPerFullTime && partTimeEmployeesPerFullTime > 0
      ? partTimeEmployeesPerFullTime
      : 2;

  return (
    getPositiveNumber(fullTimeEmployees) +
    Math.ceil(getPositiveNumber(partTimeEmployees) / partTimeConversionRate)
  );
};

export const getMembershipBasePrice = ({
  fullTimeEmployees,
  membershipModel,
  partTimeEmployees,
  partTimeEmployeesPerFullTime,
  paymentSchedule,
}: MembershipBasePriceInput): MembershipBasePriceResult => {
  if (!membershipModel) {
    return {
      amountInCents: 0,
      amountInDollars: 0,
      code: "-",
      pricingKey: "",
      weightedEmployees: 0,
    };
  }

  const weightedEmployees = getWeightedEmployeeCount({
    fullTimeEmployees,
    partTimeEmployees,
    partTimeEmployeesPerFullTime,
  });
  const isEmployeeRangePricing =
    membershipModel.pricingModel ===
      ChamberMemberProMembershipPricingModelEnum.EmployeeRange ||
    membershipModel.pricingModel === "employeeRange";

  if (!isEmployeeRangePricing) {
    const amountInDollars = getPriceInDollars(membershipModel, paymentSchedule);

    return {
      amountInCents: Math.round(amountInDollars * 100),
      amountInDollars,
      code: membershipModel.code || "-",
      pricingKey: membershipModel.id,
      weightedEmployees,
    };
  }

  const matchingRange = membershipModel.ranges?.find((range) => {
    if (range.to === null || range.to === undefined) {
      return weightedEmployees >= range.from;
    }

    return weightedEmployees >= range.from && weightedEmployees <= range.to;
  });
  const amountInDollars = matchingRange
    ? getPriceInDollars(matchingRange, paymentSchedule)
    : 0;

  return {
    amountInCents: Math.round(amountInDollars * 100),
    amountInDollars,
    code: matchingRange?.code || membershipModel.code || "-",
    pricingKey:
      matchingRange?.id && membershipModel.id
        ? `${membershipModel.id}-${matchingRange.id}`
        : membershipModel.id,
    weightedEmployees,
  };
};

export const getMembershipEffectivePrice = ({
  basePrice,
  paymentSchedule,
  proratedSignupPricingEnabled,
  proratedSignupPrices = [],
}: MembershipEffectivePriceInput): MembershipEffectivePriceResult => {
  const proratedPrice =
    paymentSchedule === ChamberMemberProPaymentScheduleEnum.Annual &&
    proratedSignupPricingEnabled
      ? proratedSignupPrices.find(
          (price) =>
            price.pricingKey === basePrice.pricingKey &&
            price.month === getCurrentMonth()
        )
      : undefined;
  const amountInCents = proratedPrice
    ? Math.round(proratedPrice.priceInDollars * 100)
    : basePrice.amountInCents;
  const proratedDiscountInCents = Math.max(
    basePrice.amountInCents - amountInCents,
    0
  );

  return {
    ...basePrice,
    amountInCents,
    amountInDollars: amountInCents / 100,
    baseAmountInCents: basePrice.amountInCents,
    baseAmountInDollars: basePrice.amountInDollars,
    proratedDiscountInCents,
    proratedDiscountInDollars: proratedDiscountInCents / 100,
  };
};
